import { FlexLayout, Typography } from '@eduact/ed-system'
import Spacer from '@eduact/ed-components/Spacer'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text as FeedbackTitle } from '@eduact/ed-components/TestSummary/OrderingAnswer/OrderingAnswer'
import { FeedbacktWrapper } from '@eduact/ed-components/TestSummary/GapAnswer/GapAnswer'
import stampAutoDir from '@Utilities/StampAutoDir'
import { QuestionContentWrapper } from './QuestionStyled'
import { StoredAnswerEntry, entryMatchesOption } from './matching'

type Props = {
	question: any
	test: any
	status: string
}

/**
 * Vendored from ed-components 0.4.10 TestSummary/MrqAnswer. Same rendering and
 * reveal gating; the per-entry join moved from text equality to the id-first
 * rule (each stored entry carries its own optional option_id — legacy and
 * importer rows have none), and stored entries matching NO live option render
 * as orphan rows — see VENDORED.md.
 */
const MrqAnswer: React.FC<Props> = ({ question, test, status }) => {
	const { t } = useTranslation(['test'])
	// Array.isArray guard: a question type-flipped from mcq leaves OBJECT-
	// shaped content behind, and .find/.filter on it throws at render.
	const rawStored = question?.answer?.content?.options
	const storedEntries = (Array.isArray(rawStored) ? rawStored : []) as StoredAnswerEntry[]

	const showAnswer = useMemo(
		() =>
			(status === 'failed' && test?.show_correct_if_failed === true) ||
			(status === 'passed' && test?.show_correct_if_passed === true),
		[status, test]
	)

	const chosenEntryFor = useCallback(
		(item: any) => storedEntries.find(entry => entryMatchesOption(entry, { id: item.id ?? null, choice: item.choice })),
		[storedEntries]
	)

	const getMcqColor = useCallback(
		(item: any) => {
			const chosenOption = chosenEntryFor(item)
			if ((!showAnswer && chosenOption?.correct === false) || (chosenOption && !chosenOption.correct)) return '#ffd5cc'
			if (!showAnswer && chosenOption?.correct) return '#aeaeae19'
			if (showAnswer && chosenOption?.correct) return '#00d66b19'
			if (showAnswer && item.is_correct) return '#00a05056'
			return '#FFF'
		},
		[chosenEntryFor, showAnswer]
	)
	const bulletColor = useCallback(
		(item: any) => {
			const chosenOption = chosenEntryFor(item)
			if ((!showAnswer && chosenOption?.correct === false) || (chosenOption && !chosenOption.correct)) return 'red'
			if (!showAnswer && chosenOption?.correct) return 'silver'
			if (showAnswer && chosenOption?.correct) return 'green'
			if (showAnswer && item.is_correct) return 'green'
			return 'silver'
		},
		[chosenEntryFor, showAnswer]
	)
	const bulletState = useCallback((item: any) => !!chosenEntryFor(item), [chosenEntryFor])

	const liveOptions = (question.options ?? []) as any[]
	const orphans = storedEntries.filter(
		entry => !liveOptions.some(item => entryMatchesOption(entry, { id: item.id ?? null, choice: item.choice }))
	)

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper dangerouslySetInnerHTML={{ __html: stampAutoDir(question.content) }} />
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel fontSize={{ sm: '0.5rem', lg: '1rem' }}>
					Answers
				</AnswersLabel>
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{liveOptions.map((mcqItem, index) => (
						<FlexLayoutStyle
							alignItems={'center'}
							key={`${mcqItem.id ?? mcqItem.choice}-${index}`}
							mb={{ sm: '0.75rem' }}
							background={getMcqColor(mcqItem)}
						>
							<AnswerBullet filled={bulletState(mcqItem)} state={bulletColor(mcqItem)} />
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label dir="auto" htmlFor={mcqItem.choice}>{mcqItem.choice}</Label>
							</Typography>
						</FlexLayoutStyle>
					))}
					{orphans.map((entry, index) => (
						<FlexLayoutStyle
							alignItems={'center'}
							key={`orphan-${entry.option_id ?? entry.answer}-${index}`}
							mb={{ sm: '0.75rem' }}
							background={entry.correct ? '#00d66b19' : '#ffd5cc'}
						>
							<AnswerBullet filled state={entry.correct ? 'green' : 'red'} />
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label dir="auto">
									{entry.answer} — {t('option_no_longer_exists')}
								</Label>
							</Typography>
						</FlexLayoutStyle>
					))}
				</FlexLayout>
			</FlexLayout>
			{question.feedback !== null && (
				<>
					<FeedbackTitle>Feedback</FeedbackTitle>
					<FeedbacktWrapper dangerouslySetInnerHTML={{ __html: stampAutoDir(question.feedback) }} />
				</>
			)}
		</Spacer>
	)
}

export default MrqAnswer

const FlexLayoutStyle = styled(FlexLayout)<{ background: string }>`
	background: ${({ background }) => background};
	height: 2.313rem;
	padding: 0.5rem;
	border-radius: 7px;
	input[type='radio']#correct {
		accent-color: #00d66b;
	}
	input[type='radio']#wrong {
		accent-color: #ff0000;
	}
`
const Label = styled.label`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.87);
	font-weight: normal;
	@media screen and (min-width: 769px) {
		font-size: 1.125rem;
	}
`
const AnswerBullet = styled.div<{ filled: boolean; state: string }>`
	border-radius: 50%;
	position: relative;
	background: ${props => (props.filled ? props.theme.colors[props.state] : '')};
	box-shadow: inset 0 0 0 1px #fff;
	outline: 1px solid ${props => (props.filled ? props.theme.colors[props.state] : 'gray')};
	width: 0.535rem;
	height: 0.535rem;
	@media screen and (min-width: 769px) {
		width: 1.25rem;
		height: 1.25rem;
		box-shadow: inset 0 0 0 3px #fff;
	}
`
const AnswersLabel = styled(Typography)`
	color: ${props => props.theme.colors.silver};
	font-size: 1rem;
	font-weight: 300;
	@media screen and (min-width: 769px) {
		margin-bottom: 0.75rem;
	}
`
